import { useCallback, useRef, useState } from "react";
import { MESSAGE_PAGE_SIZE } from "@/lib/message-limits";
import {
  MESSAGE_ABSOLUTE_LIMIT,
  MESSAGE_MAX_LIMIT,
} from "./message-area-types";

interface UseMessageLimitArgs {
  channelId: string | null;
}

interface UseMessageLimitResult {
  messageLimit: number;
  bumpInFlightRef: React.RefObject<boolean>;
  bumpLimit: (opts?: { bypassCap?: boolean }) => void;
  /** Limit decay: after a deep-jump resolves, reset to MAX when the
   *  user returns to bottom so we don't keep thousands of messages
   *  subscribed. */
  handleReturnToBottom: () => void;
  /** Render-time sync: caller invokes with the live `channelMessages`
   *  length so the in-flight flag clears the moment Zero delivers a
   *  larger slice. Decoupled from the hook signature because
   *  `messageLimit` must flow into `useChannelMessages` BEFORE the
   *  resulting slice is available. */
  syncSliceLength: (length: number) => void;
}

/**
 * Per-channel Zero query-limit state machine.
 *
 *   • `bumpLimit()` adds another `MESSAGE_PAGE_SIZE` to the channel's
 *     limit, capped at `MESSAGE_MAX_LIMIT` (or `MESSAGE_ABSOLUTE_LIMIT`
 *     when `bypassCap` is set — used by deep-link resolution).
 *   • A 3-second safety valve clears `bumpInFlightRef` if Zero never
 *     delivers a larger slice (network hiccup, query gone stale, etc.).
 *   • Reading `channelMessagesLength` during render lets us clear the
 *     in-flight flag the moment new data arrives without an effect.
 *   • `handleReturnToBottom` resets the limit to `MESSAGE_MAX_LIMIT`
 *     when the user scrolls back to bottom after deep-jumping past it.
 */
export function useMessageLimit({
  channelId,
}: UseMessageLimitArgs): UseMessageLimitResult {
  const [messageLimitByChannel, setMessageLimitByChannel] = useState<
    Record<string, number>
  >({});
  const messageLimit = channelId
    ? (messageLimitByChannel[channelId] ?? MESSAGE_PAGE_SIZE)
    : MESSAGE_PAGE_SIZE;

  const bumpInFlightRef = useRef(false);

  const bumpLimit = useCallback(
    (opts?: { bypassCap?: boolean }) => {
      if (!channelId) return;
      if (bumpInFlightRef.current) return;
      bumpInFlightRef.current = true;

      setMessageLimitByChannel((prev) => {
        const current = prev[channelId] ?? MESSAGE_PAGE_SIZE;
        const cap = opts?.bypassCap
          ? MESSAGE_ABSOLUTE_LIMIT
          : MESSAGE_MAX_LIMIT;
        if (current >= cap) {
          bumpInFlightRef.current = false;
          return prev;
        }
        return {
          ...prev,
          [channelId]: Math.min(current + MESSAGE_PAGE_SIZE, cap),
        };
      });

      // Safety valve: clear in-flight after 3s if Zero doesn't deliver
      setTimeout(() => {
        if (bumpInFlightRef.current) {
          bumpInFlightRef.current = false;
        }
      }, 3000);
    },
    [channelId],
  );

  // Clear in-flight when Zero delivers new data. The caller invokes
  // this during render with the current channelMessages length —
  // exposed as a function (not via the hook arg) because the hook
  // returns `messageLimit` which must flow into `useChannelMessages`
  // BEFORE the resulting slice is available.
  const prevSliceLengthRef = useRef(0);
  const syncSliceLength = useCallback((length: number) => {
    if (length !== prevSliceLengthRef.current) {
      prevSliceLengthRef.current = length;
      bumpInFlightRef.current = false;
    }
  }, []);

  const handleReturnToBottom = useCallback(() => {
    if (channelId) {
      setMessageLimitByChannel((prev) => {
        const current = prev[channelId] ?? MESSAGE_PAGE_SIZE;
        if (current > MESSAGE_MAX_LIMIT) {
          return {
            ...prev,
            [channelId]: MESSAGE_MAX_LIMIT,
          };
        }
        return prev;
      });
    }
  }, [channelId]);

  return {
    messageLimit,
    bumpInFlightRef,
    bumpLimit,
    handleReturnToBottom,
    syncSliceLength,
  };
}
